import { FC, useState, useEffect } from "react";
import Button from "../form/Button";
import Input from "../form/Input";
import useClickByKey from "../../hooks/useClickByKey";
import {
  DATE_PRESETS,
  IDateRange,
  presetForRange,
  presetRange,
  TDatePreset,
} from "./orderDatePresets";

/**
 * Date range for a categorised report.
 *
 * The same presets and the same apply-on-Apply behaviour as the Recent Orders
 * toolbar, without the per-column search - there is nothing to search here,
 * the rows are days.
 */
const OrdersRangeFilter: FC<{
  range: IDateRange,
  onChange: (range: IDateRange) => void,
  disabled?: boolean,
}> = ({
  range,
  onChange,
  disabled = false,
}) => {
  const [draft, setDraft] = useState<IDateRange>(() => range);
  const { btnRef, onKeyUp } = useClickByKey();

  useEffect(() => setDraft(() => range), [range]);

  const selectedPreset = presetForRange(draft);

  const handlePreset = (value: string) => {
    const preset = value as TDatePreset;
    if (preset === 'custom') return;

    const next = presetRange(preset);
    setDraft(() => next);
    onChange(next);
  };

  const isDirty = draft.createdFrom !== range.createdFrom || draft.createdTill !== range.createdTill;

  return (
    <div className="flex flex-wrap gap-3 items-end grow">
      <Input
        label="Period"
        name="datePreset"
        size="small"
        className="w-[130px]"
        value={selectedPreset}
        disabled={disabled}
        handle={(e) => handlePreset(e.target.value)}
        options={[
          ...DATE_PRESETS.map(({ value, title }) => ({ value, title })),
          ...(selectedPreset === 'custom' ? [{ value: 'custom', title: 'Custom' }] : []),
        ]}
      />

      <Input
        label="From"
        name="createdFrom"
        type="date"
        size="small"
        className="w-[150px]"
        value={draft.createdFrom}
        disabled={disabled}
        handle={(e) => setDraft((prev) => ({ ...prev, createdFrom: e.target.value }))}
        onKeyUp={onKeyUp}
      />

      <Input
        label="Till"
        name="createdTill"
        type="date"
        size="small"
        className="w-[150px]"
        value={draft.createdTill}
        disabled={disabled}
        handle={(e) => setDraft((prev) => ({ ...prev, createdTill: e.target.value }))}
        onKeyUp={onKeyUp}
      />

      <Button
        btnRef={btnRef}
        size="small"
        type="submit"
        isNotFullWidth
        disabled={disabled || !isDirty}
        onClick={() => onChange(draft)}
        className="mb-[2px] px-6"
      >
        Apply
      </Button>
    </div>
  );
};

export default OrdersRangeFilter;
